import React, { useCallback, useRef, useState } from 'react'

import memoizeOne from 'memoize-one'
import { useConnect } from 'redux-bundler-hook'

import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete'

import createLogger from '~/src/Lib/Logging'
import { noop } from '~/src/Lib/Utils'
import TextField from '~/src/UI/Shared/Form/TextField'

const displayName = 'IPM/PesticideField'
const logger = createLogger(displayName)

const filterPesticideOptions = createFilterOptions({
  stringify: ({ name, activeIngredient }) => [name, activeIngredient].filter(Boolean).join(' ')
})
const getPesticideOptions = memoizeOne(pesticides => Object.values(pesticides))
const getPesticideOptionLabel = option => {
  if (typeof option === 'string') {
    return option
  }
  return option.inputValue
    ? `Add "${option.inputValue}"`
    : `${option.name}${option.activeIngredient ? ` (${option.activeIngredient})` : ''}`
}
const getOptionSelected = (option, value) => option.id === value.id

export const PesticideFieldComponent = props => {
  const { disabled, field, form, autoFocus, pesticides, required, onAddPesticide } = props
  const { name } = field
  const { setFieldValue } = form
  const [inputValue, setInputValue] = useState(null)
  const defaultOption = useRef(null)
  const onInputChange = useCallback((_, newValue) => setInputValue(newValue), [setInputValue])
  const addNewPesticide = useCallback(pesticideName => {
    if (onAddPesticide) onAddPesticide(pesticideName)
  }, [onAddPesticide])
  const filterOptions = useCallback((options, params) => {
    const filtered = filterPesticideOptions(options, params)
    if (filtered.length === 1) {
      defaultOption.current = filtered[0]
    } else {
      defaultOption.current = null
    }

    if (onAddPesticide && !filtered.length && params.inputValue) {
      filtered.push({ inputValue: params.inputValue })
    }

    return filtered
  }, [onAddPesticide])

  const setDefaultOnEnter = useCallback(({ key }) => {
    if (key === 'Enter') {
      if (!defaultOption.current) return
      setFieldValue(name, defaultOption.current)
    }
  }, [name, setFieldValue])

  return (
    <Autocomplete
      autoComplete
      autoSelect
      openOnFocus={false}
      disabled={Boolean(disabled)}
      value={field.value}
      noOptionsText="No matches"
      options={getPesticideOptions(pesticides)}
      onChange={(_, newValue) => {
        if (onAddPesticide && newValue && newValue.inputValue) {
          addNewPesticide(newValue.inputValue)
          return
        }
        setInputValue(null)
        form.setFieldValue(field.name, newValue)
      }}
      onClose={(_, reason) => {
        if (reason === 'select-option' || reason === 'escape') return
        if (defaultOption.current) {
          form.setFieldValue(field.name, defaultOption.current)
          return
        }
        if (inputValue && !field.value) {
          addNewPesticide(inputValue)
        }
      }}
      onInputChange={onInputChange}
      filterOptions={filterOptions}
      getOptionLabel={getPesticideOptionLabel}
      isOptionEqualToValue={getOptionSelected}
      renderInput={inputProps => (
        <TextField
          {...inputProps}
          autoFocus={Boolean(autoFocus)}
          margin="dense"
          fullWidth
          required={Boolean(required)}
          label="Pesticide"
          onChange={noop}
          onKeyPress={setDefaultOnEnter}
        />
      )}
      style={{ width: '100%' }}
    />
  )
}
PesticideFieldComponent.displayName = displayName

export default React.memo(props => {
  const { pesticides } = useConnect('selectPesticides')

  return <PesticideFieldComponent {...props} pesticides={pesticides} />
})
